import type { EpocusEncounter } from "$global-providers/rxdb-provider/collections/epocus-encounter";
import type { EpocusExam } from "$global-providers/rxdb-provider/collections/epocus-exam";
import type { EpocusReview } from "$global-providers/rxdb-provider/collections/epocus-review";
import type { SelectableAgreement } from "../../../_lib/review-agreement";
import { isEscalating } from "../../../_lib/review-agreement";

export const MAJOR_DISCREPANCY_ESCALATION_REASON =
  "Major discrepancy at sign-off";

export type ReviewInput = {
  id: string;
  exam: EpocusExam;
  reviewerId: string;
  agreement: SelectableAgreement;
  imageQualityScore: number;
  feedbackTags: readonly string[];
  comment: string;
  now: string;
  createdAt?: string;
};

export const buildUnsignedReview = ({
  id,
  exam,
  reviewerId,
  agreement,
  imageQualityScore,
  feedbackTags,
  comment,
  now,
  createdAt,
}: ReviewInput): EpocusReview => ({
  id,
  examId: exam.id,
  reviewerId,
  agreement,
  imageQualityScore,
  qualityScore: imageQualityScore,
  signedAt: null,
  feedbackTags: [...feedbackTags],
  comment: comment.trim() || null,
  meta: {
    createdAt: createdAt ?? now,
    createdByUserId: reviewerId,
    updatedAt: now,
    updatedByUserId: reviewerId,
  },
});

export const signReview = (
  review: EpocusReview,
  now: string,
): EpocusReview => ({
  ...review,
  signedAt: now,
  meta: {
    ...review.meta,
    updatedAt: now,
    updatedByUserId: review.reviewerId,
  },
});

export const buildSignedReview = (input: ReviewInput): EpocusReview =>
  signReview(buildUnsignedReview(input), input.now);

type ExamSignOffState = Pick<EpocusExam, "id" | "status">;
type ReviewSignOffState = Pick<EpocusReview, "examId" | "signedAt">;

export const everyExamReviewed = ({
  exams,
  reviews,
}: {
  exams: readonly ExamSignOffState[];
  reviews: readonly ReviewSignOffState[];
}): boolean => {
  const submitted = exams.filter((exam) => exam.status === "submitted");
  if (submitted.length === 0) return false;

  const signed = new Set(
    reviews.filter((review) => review.signedAt).map((review) => review.examId),
  );

  return submitted.every((exam) => signed.has(exam.id));
};

export const buildEncounterSignOffPatch = ({
  encounter,
  agreement,
  everyExamReviewed,
  now,
}: {
  encounter: EpocusEncounter;
  agreement: SelectableAgreement;
  everyExamReviewed: boolean;
  now: string;
}): Partial<EpocusEncounter> => {
  const escalatedAt = encounter.escalatedAt ?? null;
  const escalating = isEscalating(agreement) || escalatedAt !== null;

  return {
    status: everyExamReviewed ? "complete" : "pending_review",
    reviewOutcome: escalating ? "escalated" : "signed",
    escalatedAt: escalating ? (escalatedAt ?? now) : null,
    escalationReason: escalating
      ? (encounter.escalationReason ?? MAJOR_DISCREPANCY_ESCALATION_REASON)
      : null,
  };
};
